/* OnSumn v2 — Glass Header */

/* The `CITIES` const that stood here — a hardcoded
   ['ALL','OAKLAND','SF','SAN JOSE','VALLEJO','RICHMOND','DALY CITY'] — is
   gone (2026-08-17). It was stale in BOTH directions and had been for months:
   BERKELEY carried 41 live events with no pill at all (reachable only by
   hand-typing ?city=), while VALLEJO held a permanent slot on 1 event. A
   declared list of filter options over an undeclared list of cities can only
   ever be wrong in one of those two ways. The pill set is now DERIVED from the
   feed and passed in as `cities`, ranked by row count. It also published
   `window.CITIES`, which src/entry/index-main.jsx used as the ?city= deep-link
   allowlist; that gate now resolves against the same derived vocabulary
   through lib/city-registry#resolveCityToken. */
import { ALL_CITIES, NAV_FALLBACK_CITIES, cityToTag, normalizeCityKey } from '../lib/city-registry.js';

/* Inline SVG location pin */
function LocationPin({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 6, marginTop: -1 }}>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
      <circle cx="12" cy="10" r="3"/>
    </svg>
  );
}

/* The rail vocabulary is DECLARED — NAV_FALLBACK_CITIES, curated by Michael
   (2026-08-20) — on BOTH surfaces (desktop rail + mobile drawer). The
   derived-top-6 rail that stood here for three days kept promoting cities
   Michael didn't want as chrome (Napa, Rohnert Park) and dropped the
   original ones (Vallejo, Richmond, Daly City); the ruling is that the
   header pill set is an editorial choice, edited in ONE place
   (lib/city-registry.js#NAV_FALLBACK_CITIES), not a ranking output. The
   derived vocabulary still exists where it belongs: the homepage footer and
   ?city= deep-link resolution — a Napa row stays reachable, it just isn't
   header chrome. Same-day sibling ruling still in force: the rail is FIXED —
   the "+N MORE" expansion stays deleted (it never wrapped; one click blew
   the header out past the viewport). Gated: tests/header-nav.test.js
   "declared vocabulary". */

function Header({ darkMode, activeCity, setActiveCity, onToggleDark, radius, activePage, cities }) {
  const [scrolled, setScrolled] = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  // Initialized from the media query DURING RENDER, not corrected in an
  // effect. useState(false) meant a phone painted the desktop header first
  // and reflowed a tick later — a measured 0.25 layout shift (see
  // lib/breakpoints.js#isBelow and tests/mobile-first-render.test.js).
  const [isMobile, setIsMobile] = React.useState(
    () => (typeof window !== 'undefined' && window.bp ? window.bp.isMobile() : false),
  );

  React.useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);

  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 767.98px)');
    const update = () => setIsMobile(mq.matches);
    update();
    if (mq.addEventListener) mq.addEventListener('change', update);
    else if (mq.addListener) mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', update);
      else if (mq.removeListener) mq.removeListener(update);
    };
  }, []);

  /* `cities` (the derived feed vocabulary) now serves ONE purpose here: its
     presence says "this page has a filterable feed". The pills themselves are
     the DECLARED list — see the header comment above. A page that mounts
     Header without the prop (venue, flows) has nothing to filter and gets no
     rail at all, same as before. */
  const hasFeed = Array.isArray(cities) && cities.filter(Boolean).length > 0;
  const railCities = hasFeed ? NAV_FALLBACK_CITIES : [];

  /* Pill identity. normalizeCityKey on BOTH sides — the whole point of the
     shared key is that "San Jose" from the feed and "SAN JOSE" from a deep
     link are one pill, not two, and never a silently-inactive one. */
  const isActiveCity = (c) => normalizeCityKey(activeCity) === normalizeCityKey(c);
  /* Chip text is the TAG (SF, OAKLAND, DALY CITY); the VALUE handed to
     setActiveCity is the canonical city, because the filter keys on the claim.
     Never the reverse — a tag in state would make "SF" a thing the feed has
     to know how to match. */
  const cityLabel = (c) => (c === ALL_CITIES ? 'ALL' : cityToTag(c));

  const glass = glassStyle(darkMode, 'panel');
  const textColor = darkMode ? '#fff' : '#0A0A0A';
  // The "Where to" hint stays muted (it's secondary label copy). City pill
  // text gets a HIGH-contrast inactive state so the pills don't look broken
  // when scanned, plus a real hover state so the click affordance lands.
  const labelMutedColor = darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.55)';
  const cityInactiveColor = darkMode ? 'rgba(255,255,255,0.78)' : 'rgba(0,0,0,0.72)';
  const cityHoverBg = darkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.04)';
  const cityActiveBg = darkMode ? 'rgba(255,210,128,0.18)' : 'rgba(217,148,46,0.16)';
  const cityActiveColor = darkMode ? 'var(--amber-gold-light, #FFD280)' : 'var(--amber-gold-ink)';

  return (
    <header style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
      padding: scrolled ? '0 16px' : '8px 16px',
      transition: 'padding 0.4s ease',
    }}>
      <div style={{
        maxWidth: 1280, margin: '0 auto',
        ...glass,
        borderRadius: radius,
        border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)',
        // 3-column flex: [left logo+cities] [center BAY FLOWS] [right search+drop+toggle].
        // Desktop: center column flex:1's the slack so BAY FLOWS doesn't bunch
        // up against the city pills (used to feel left-heavy next to the wide
        // SearchBar on the right).
        // Mobile: center column is hidden + dividers hidden, so we pin the
        // left/right clusters to opposite edges via space-between. Inner
        // padding tightens 24→14 to give the right cluster room to breathe.
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between',
        padding: isMobile ? '10px 14px' : '12px 24px',
        transition: 'border-radius 0.4s, background 0.4s',
        marginTop: scrolled ? 0 : 8,
      }}>
        {/* Left: Logo + city nav. Both dividers (around the city cluster)
            now use a bumped opacity so they actually read against the glass
            chrome — the prior 0.1/0.08 was barely visible. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flex: '0 0 auto' }}>
          {/* 2026-07-16: was a <span cursor:pointer> with no href — a dead
              element wearing a clickable costume. Real link home now (matters
              on /flows and every non-home page; harmless self-nav on /). */}
          <a href="/" aria-label="OnSumn — home" style={{
            fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 900,
            color: textColor, letterSpacing: '-0.02em', textTransform: 'uppercase',
            cursor: 'pointer', textDecoration: 'none', display: 'inline-block',
          }}>OnSumn</a>
          {/* The whole city cluster — leading divider, nav, trailing divider —
              is gated on the page HAVING a feed. /flows and /venue mount this
              Header with no feed, and a "Where to" label above a lone ALL pill
              is a filter with one option: chrome that looks broken rather than
              chrome that is honest. The dividers travel with the nav so a
              hidden cluster can't leave two rules touching. */}
          {railCities.length > 0 && (
          <React.Fragment>
          <div className="header-divider-desktop" style={{ width: 1, height: 20, background: darkMode ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.16)' }}/>
          {/* City nav — with inline "Where to" label so the pills are
              discoverable. Pre: unlabeled buttons sitting between two
              dividers, easy to scan past without realizing they're a
              city selector. Mobile menu has a "Where To" header; this
              brings parity to the desktop experience. */}
          <nav
            /* A fixed single row — ALL + the declared list, nothing that can
               grow it. See the rail-vocabulary note above for why the
               expansion is gone. */
            style={{ display: 'flex', gap: 4, alignItems: 'center' }}
            className="city-nav-desktop"
            aria-label="Filter by city"
          >
            <span aria-hidden="true" style={{
              fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 500,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              color: labelMutedColor,
              marginRight: 6,
            }}>Where to</span>
            {[ALL_CITIES, ...railCities].map(c => {
              const isActive = isActiveCity(c);
              return (
                <button
                  key={c}
                  aria-label={`Show events in ${c === ALL_CITIES ? 'all cities' : c}`}
                  aria-pressed={isActive}
                  onClick={() => {
                    setActiveCity(c);
                    // Scroll the events section into view so the user
                    // SEES the filter happen instead of having to scroll
                    // down themselves. scroll-margin-top on #events
                    // (set in Map.jsx) keeps it clear of the fixed Header.
                    if (typeof document !== 'undefined') {
                      const target = document.getElementById('events');
                      if (target && typeof target.scrollIntoView === 'function') {
                        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) e.currentTarget.style.background = cityHoverBg;
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) e.currentTarget.style.background = 'transparent';
                  }}
                  style={{
                    fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: isActive ? 800 : 600,
                    letterSpacing: '0.06em', textTransform: 'uppercase',
                    // WCAG 2.2 SC 2.5.8 — 24×24 CSS-pixel minimum bounding box.
                    // Vertical padding bumped 6→8 so the box clears 24px without
                    // changing visual rhythm enough to break the divider alignment.
                    padding: '8px 10px', borderRadius: Math.max(radius - 4, 0),
                    minHeight: 24,
                    background: isActive ? cityActiveBg : 'transparent',
                    color: isActive ? cityActiveColor : cityInactiveColor,
                    border: 'none', cursor: 'pointer',
                    transition: 'background 200ms ease, color 200ms ease, transform 120ms ease',
                  }}
                  onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.96)'; }}
                  onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                >{cityLabel(c)}</button>
              );
            })}
          </nav>
          {/* Divider — closes the city cluster. Visually answers "where do
              the cities end?" so BAY FLOWS doesn't read as another pill.
              Hidden on mobile (city pills + BAY FLOWS both hidden under md). */}
          <div className="header-divider-desktop" style={{ width: 1, height: 20, background: darkMode ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.16)' }}/>
          </React.Fragment>
          )}
        </div>

        {/* Center: the two destinations, BAY FLOWS + CALENDAR. The WEEKLY nav
            link was retired 2026-08-05 (G7/E1, Marco: "It definitely does not
            need to be there"; the /#weekly section, anchor, and 21%-depth
            placement all stay). CALENDAR joined 2026-09-29: the calendar sat
            2.1 screens below the fold as the 4th toggle option and nobody
            found it. The flex:1 STAYS: it absorbs the slack so the pair
            centers between the city pills and the SearchBar instead of
            bunching left (see the 3-column comment above). */}
        <div className="bay-flows-center" style={{
          flex: '1 1 auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 12,
          minWidth: 0,
        }}>
          <a href="/flows" style={{
            display: 'inline-flex', alignItems: 'center',
            // 2026-07-16: 11px body type read lost in the flex:1 center slack
            // ("too much negative space" — Michael). Display face at 14/800
            // gives the label real presence; wordmark (20/900) still leads.
            fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 800,
            letterSpacing: '0.08em', textTransform: 'uppercase',
            // WCAG 2.2 SC 2.5.8 — 24×24 CSS-pixel minimum. Vertical padding
            // bumped 6→8 + min-height 24 so the link clears the floor.
            padding: '8px 12px', borderRadius: Math.max(radius - 4, 0),
            minHeight: 24,
            background: activePage === 'flows' ? 'var(--international-orange)' : 'transparent',
            color: activePage === 'flows' ? 'var(--on-international-orange)' : (darkMode ? 'var(--amber-gold-light)' : 'var(--amber-gold-ink)'),
            textDecoration: 'none', transition: 'all 0.25s',
            whiteSpace: 'nowrap',
          }}>BAY FLOWS</a>
          <a href="/?view=calendar" style={{
            display: 'inline-flex', alignItems: 'center',
            fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 800,
            letterSpacing: '0.08em', textTransform: 'uppercase',
            padding: '8px 12px', borderRadius: Math.max(radius - 4, 0),
            minHeight: 24,
            background: activePage === 'calendar' ? 'var(--international-orange)' : 'transparent',
            color: activePage === 'calendar' ? 'var(--on-international-orange)' : (darkMode ? 'var(--amber-gold-light)' : 'var(--amber-gold-ink)'),
            textDecoration: 'none', transition: 'all 0.25s',
            whiteSpace: 'nowrap',
          }}>CALENDAR</a>
        </div>

        {/* Right — on mobile, grow to claim slack between ONSUMN and the
            right edge so the SearchBar (flex:1 inside) can extend. Desktop
            stays content-width and lets the center BAY FLOWS spacer handle
            slack distribution. */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          flex: isMobile ? '1 1 auto' : '0 0 auto',
          marginLeft: isMobile ? 12 : 0,
          minWidth: 0,
        }}>
          {typeof window !== 'undefined' && window.SearchBar && (
            <window.SearchBar darkMode={darkMode} isMobile={isMobile} />
          )}
          <button onClick={onToggleDark} className="theme-toggle-desktop focus-double-ring" aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'} style={{
            ...glassStyle(darkMode, 'btn'),
            border: 'none', cursor: 'pointer',
            color: textColor, fontSize: 16, padding: '8px 10px',
            minHeight: 44, minWidth: 44,
            borderRadius: radius, display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.25s',
          }} title={darkMode ? 'Light mode' : 'Dark mode'}>
            {darkMode ? '☀' : '☾'}
          </button>
          {/* Mobile hamburger */}
          <button className="mobile-menu-trigger focus-double-ring" aria-label="Open menu" onClick={() => setMobileOpen(true)} style={{
            ...glassStyle(darkMode, 'btn'),
            border: 'none', cursor: 'pointer',
            color: textColor, fontSize: 20, padding: '8px 10px',
            minHeight: 44, minWidth: 44,
            borderRadius: radius, alignItems: 'center', justifyContent: 'center',
          }}>☰</button>
        </div>
      </div>

      {/* Mobile menu overlay */}
      <div className={`mobile-overlay ${mobileOpen ? 'open' : ''}`} onClick={() => setMobileOpen(false)}>
        <div className="mobile-panel" onClick={e => e.stopPropagation()} style={{
          ...glassStyle(darkMode, 'panel'),
          background: darkMode ? 'rgba(10,10,10,0.95)' : 'rgba(245,245,245,0.97)',
          borderLeft: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <span style={{
              fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 900,
              color: textColor, textTransform: 'uppercase',
            }}>Where To</span>
            <button onClick={() => setMobileOpen(false)} style={{
              background: 'none', border: 'none', color: labelMutedColor,
              fontSize: 24, cursor: 'pointer', minHeight: 44, minWidth: 44,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>×</button>
          </div>
          {/* Same DECLARED list as the desktop rail — one vocabulary, two
              surfaces. (Until 2026-08-20 this drawer carried the full derived
              ~26; Michael's curation made the rail editorial, and a drawer
              that contradicts the rail is two vocabularies again.) */}
          {railCities.length > 0 && [ALL_CITIES, ...railCities].map(c => {
            const isActive = isActiveCity(c);
            return (
              <button key={c} aria-pressed={isActive} onClick={() => {
                setActiveCity(c);
                setMobileOpen(false);
                if (typeof document !== 'undefined') {
                  const target = document.getElementById('events');
                  if (target && typeof target.scrollIntoView === 'function') {
                    // Defer one frame so the mobile overlay close animation
                    // doesn't fight the scroll.
                    requestAnimationFrame(() => {
                      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    });
                  }
                }
              }} style={{
                display: 'block', width: '100%', textAlign: 'left',
                fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: isActive ? 800 : 600,
                letterSpacing: '0.04em', textTransform: 'uppercase',
                padding: '14px 16px', minHeight: 48, marginBottom: 4,
                borderRadius: Math.max(radius - 4, 8),
                background: isActive ? cityActiveBg : 'transparent',
                color: isActive ? cityActiveColor : cityInactiveColor,
                border: 'none', cursor: 'pointer',
                transition: 'all 0.2s',
              }}>{cityLabel(c)}</button>
            );
          })}
          <div style={{ height: 1, background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', margin: '12px 0' }} />
          <a href="/flows" onClick={() => setMobileOpen(false)} style={{
            display: 'block', width: '100%', textAlign: 'left',
            fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 700,
            letterSpacing: '0.04em', textTransform: 'uppercase',
            padding: '14px 16px', minHeight: 48,
            borderRadius: Math.max(radius - 4, 8),
            background: activePage === 'flows' ? 'var(--international-orange)' : 'transparent',
            color: activePage === 'flows' ? 'var(--on-international-orange)' : (darkMode ? 'var(--amber-gold-light)' : 'var(--amber-gold-ink)'),
            textDecoration: 'none', transition: 'all 0.2s',
          }}>BAY FLOWS</a>
          <a href="/?view=calendar" onClick={() => setMobileOpen(false)} style={{
            display: 'block', width: '100%', textAlign: 'left',
            fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 700,
            letterSpacing: '0.04em', textTransform: 'uppercase',
            padding: '14px 16px', minHeight: 48, marginTop: 4,
            borderRadius: Math.max(radius - 4, 8),
            background: activePage === 'calendar' ? 'var(--international-orange)' : 'transparent',
            color: activePage === 'calendar' ? 'var(--on-international-orange)' : (darkMode ? 'var(--amber-gold-light)' : 'var(--amber-gold-ink)'),
            textDecoration: 'none', transition: 'all 0.2s',
          }}>CALENDAR</a>
          {/* Theme toggle — lives in the hamburger panel on mobile so the
              narrow header bar isn't carrying a sun/moon button it doesn't
              need. Label flips to describe the destination state, matching
              the inline button's aria-label convention. */}
          <button
            onClick={() => { onToggleDark(); setMobileOpen(false); }}
            aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            style={{
              display: 'block', width: '100%', textAlign: 'left',
              fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 700,
              letterSpacing: '0.04em', textTransform: 'uppercase',
              padding: '14px 16px', minHeight: 48, marginTop: 4,
              borderRadius: Math.max(radius - 4, 8),
              background: 'transparent',
              color: textColor,
              border: 'none', cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            <span aria-hidden="true" style={{ marginRight: 10 }}>{darkMode ? '☀' : '☾'}</span>
            {darkMode ? 'LIGHT MODE' : 'DARK MODE'}
          </button>
        </div>
      </div>
    </header>
  );
}
Header.defaultProps = { activePage: 'home', cities: [] };
Header.propTypes = {
  darkMode: PropTypes.bool.isRequired,
  activeCity: PropTypes.string.isRequired,
  setActiveCity: PropTypes.func.isRequired,
  onToggleDark: PropTypes.func.isRequired,
  radius: PropTypes.number.isRequired,
  activePage: PropTypes.string,
  /* Canonical city names from whichever page owns the feed. Since the
     2026-08-20 curation this prop only signals FEED PRESENCE — the rendered
     pills are the declared NAV_FALLBACK_CITIES. Optional by design: /venue
     and /flows mount this Header without it and correctly show no rail. */
  cities: PropTypes.arrayOf(PropTypes.string),
};

window.Header = Header;
// window.CITIES is gone with the hardcoded list. Nothing may read a declared
// city vocabulary off the global object again — that global WAS the deep-link
// allowlist, which is how ?city=BERKELEY got rejected while 41 Berkeley events
// sat in the feed.
